/** @file Crossfades an entry's effective sample into its mixing-from result per channel (spec 5.7). */
import { angle } from '@pose2d/math/angle';
import { blendChannel } from '../anim/blendChannel';
import { channelClass } from '../anim/channelClass';
import { POSE_LAYOUT as L } from '../contracts/pose';
import type { Sample } from '../contracts/sample';
import type { TrackEntry } from '../contracts/state';

/** Linear from -> to at weight m, exactly `to` at m >= 1 (the kernel's absolute replace, W2-06). */
function mixLinear(from: number, to: number, m: number): number {
  return blendChannel(3, from, to, 0, m, 'replace');
}

/**
 * Rotation from -> to at weight m, exactly `to` at m >= 1. Without dirs: the kernel's shortest arc (W2-06). With
 * dirs (R10-05; CR-010 rewords the frozen CR-007 field): dirs[bone] holds the last *unwrapped* difference to - from
 * in degrees (0 before the first frame), and this frame's difference is the candidate w + 360k nearest to it. So the
 * first frame takes the shortest arc, and later frames stay continuous while each frame's change of to - from stays
 * under 180 degrees: a target that sweeps past +-180 keeps turning the same way, a step across 0 takes the short way.
 * A non-finite difference (a NaN or infinite frame) takes the plain shortest arc and leaves dirs untouched, so only
 * finite differences are ever stored and the next finite frame recovers.
 */
function mixAngle(from: number, to: number, m: number, dirs: Float64Array | null, bone: number): number {
  const w = angle.wrap180(to - from);
  if (dirs === null || !Number.isFinite(w)) return blendChannel(0, from, to, 0, m, 'replace');
  const diff = w + 360 * Math.round(((dirs[bone] ?? 0) - w) / 360);
  dirs[bone] = diff;
  return m >= 1 ? to : from + diff * m;
}

/** Discrete value channel c: once the threshold is reached, `to` if keyed, else unkeyed (base); before it, `from`. */
function crossDiscreteValue(r: Sample, s: Sample, c: number, takeTo: boolean, inTo: boolean): void {
  if (!takeTo) return;
  r.mask[c] = inTo ? 1 : 0;
  if (inTo) r.values[c] = s.values[c] ?? 0;
}

/** Inherit and attachments switch at attachmentThreshold, draw order at drawOrderThreshold (unkeyed codes = base). */
function crossDiscrete(r: Sample, s: Sample, m: number, entry: TrackEntry): void {
  if (m >= entry.attachmentThreshold) {
    r.inherit.set(s.inherit);
    r.attachments.set(s.attachments);
  }
  if (m >= entry.drawOrderThreshold) {
    r.drawOrderKeyed = s.drawOrderKeyed;
    if (s.drawOrderKeyed) r.drawOrder.set(s.drawOrder);
  }
}

/** One deform buffer: both keyed lerp(from, to); one side keyed lerps with the base (0 when unkeyed). */
function mixDeform(out: Float64Array, to: Float64Array, under: Float64Array | undefined, inFrom: boolean, inTo: boolean, m: number): void {
  for (let j = 0; j < out.length; j++) {
    const b = under?.[j] ?? 0;
    out[j] = mixLinear(inFrom ? (out[j] ?? 0) : b, inTo ? (to[j] ?? 0) : b, m);
  }
}

/** Deform buffers keyed by either side. */
function crossDeform(r: Sample, s: Sample, base: Sample, m: number): void {
  for (let i = 0; i < r.deform.length; i++) {
    const inFrom = r.deformKeyed[i] === 1;
    const inTo = s.deformKeyed[i] === 1;
    const out = r.deform[i];
    const to = s.deform[i];
    if ((!inFrom && !inTo) || out === undefined || to === undefined) continue;
    mixDeform(out, to, base.deformKeyed[i] === 1 ? base.deform[i] : undefined, inFrom, inTo, m);
    r.deformKeyed[i] = 1;
  }
}

/**
 * r := crossfade(from = r, to = s) at weight m over base: both keyed -> mix(from, to); only to -> mix(base, to);
 * only from -> mix(from, base). Rotation unwraps against the last difference in entry.mixDirs (the only state
 * written, CR-010); scale is a plain lerp, so a 1 -> -1 flip collapses through 0 mid-fade (documented choice,
 * research 9.2.7); discrete channels follow the entry's thresholds (W2-07). r's mask becomes the union.
 */
export function crossfadeSample(r: Sample, s: Sample, base: Sample, m: number, entry: TrackEntry, slotStart: number, ikStart: number): void {
  for (let c = 0; c < r.mask.length; c++) {
    const inFrom = (r.mask[c] ?? 0) !== 0;
    const inTo = (s.mask[c] ?? 0) !== 0;
    if (!inFrom && !inTo) continue;
    const cls = channelClass(c, slotStart, ikStart);
    if (cls === 4) {
      crossDiscreteValue(r, s, c, m >= entry.attachmentThreshold, inTo);
      continue;
    }
    const b = base.values[c] ?? 0;
    const f = inFrom ? (r.values[c] ?? 0) : b;
    const t = inTo ? (s.values[c] ?? 0) : b;
    r.values[c] = cls === 0 ? mixAngle(f, t, m, entry.mixDirs, Math.floor(c / L.LOCAL_STRIDE)) : mixLinear(f, t, m);
    r.mask[c] = 1;
  }
  crossDiscrete(r, s, m, entry);
  crossDeform(r, s, base, m);
}
